import { HeadContent, Outlet, useLocation } from '@tanstack/react-router';
import ms from 'ms';
import { useLayoutEffect, useRef } from 'react';

import { useAutoBackupLocalStorage, useQueryUserData } from '~/data/user';

import { useDispatch } from '~/redux/store';
import { saveUserId, setUserHasLoggedInViaSSO } from '~/redux/userinfoSlice';

import AuthService from '~/services/auth.service';

import { globalState } from '~/modules/applicationState';
import { DeliveryNoteUpdatesHandler } from '~/modules/deliveryNote/DeliveryNoteUpdatesHandler';

import { isPublicRoute } from '~/utils/route';

import { Spinner } from '@vestigas/ui/molecules';
import { DeactivatedAccountModal } from '~/components/DeactivatedAccountModal';
import { PublicRouteRootLayout } from '~/ui/layouts/PublicRouteRootLayout';

import { useLoadData } from './useLoadData';
import { useNotifyDataExchangeIssues } from './useNotifyDataExchangeIssues';

/**
 * RootLayout
 *
 * Wrapper component that routes between public and authenticated layouts.
 * This pattern avoids Rules of Hooks violations by conditionally rendering
 * either PublicRouteLayout or AppInitializer, rather than conditionally
 * calling hooks within a single component.
 */
export function RootLayout() {
  const location = useLocation();

  if (isPublicRoute(location.pathname)) {
    return <PublicRouteRootLayout />;
  }

  return <AppInitializer />;
}

/**
 * AppInitializer
 *
 * This component is responsible for initializing the application, handling user authentication,
 * loading necessary data, and rendering the appropriate view based on the user's status and role.
 *
 * It manages the following tasks:
 * - Handles user authentication
 * - Loads required data upon successful login
 * - Initializes third-party services (e.g., Sentry)
 * - Dispatches user information to Redux store
 * - Notifies about data exchange issues
 * - Renders special views for specific user roles (supplier overview, BayWa screen)
 * - Renders the main application view (AppLayout and DeactivatedAccountModal) for regular users
 *
 * Note: This component is only rendered for authenticated routes.
 * Public routes are handled by PublicRouteLayout via the RootLayout wrapper.
 */
function AppInitializer() {
  const dispatch = useDispatch();

  const { loadData } = useLoadData();

  const isInitializedRef = useRef(false);

  const userId = AuthService.getUserIdFromAccessToken();

  const isLoggedIn = Boolean(userId);

  const { data: currentUser } = useQueryUserData(true, {
    enabled: isLoggedIn,
    select: (data) => data,
  });

  useLayoutEffect(() => {
    if (!isLoggedIn || !currentUser) {
      return;
    }

    globalState.trigger.setCurrentUser({
      currentUser,
      hasLoggedInViaSso: AuthService.accessTokenContainsFicKey(),
    });

    if (isInitializedRef.current) {
      return;
    }

    // Load all required data once when the user logs in.
    loadData();

    dispatch(setUserHasLoggedInViaSSO(AuthService.accessTokenContainsFicKey()));

    isInitializedRef.current = true;
  }, [currentUser, dispatch, isLoggedIn, loadData]);

  useNotifyDataExchangeIssues();

  // Back up localStorage periodically as a safety measure during migration of user settings persistence.
  useAutoBackupLocalStorage({
    enabled: isLoggedIn && Boolean(currentUser),
    backupInterval: ms('1m'),
  });

  useLayoutEffect(() => {
    if (userId) {
      dispatch(saveUserId(userId));
    }
  }, [userId, dispatch]);

  if (!isLoggedIn) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <DeliveryNoteUpdatesHandler />
      <DeactivatedAccountModal />
      <HeadContent />
      <Outlet />
    </>
  );
}
